import React, { useState, useRef, useEffect } from "react";
import i18n from "@/utils/i18n";
import Input from "@/components/ui/input";
import Button from "@/components/ui/button";
import Dropdown from "@/components/ui/dropdown";
import DatePicker from "@/components/ui/datepicker";
import {
  Search as SearchIcon,
  CardView as CardViewIcon,
  ListView as ListViewIcon,
  Close as CloseIcon,
  Plus as PlusIcon,
  ArrowsSort as ArrowsSortIcon,
  Adjustments as AdjustmentsIcon,
} from "@/components/icons";
import { PageHeaderProps, FilterField, SortOption } from "@/types/component";
import "./index.scss";

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  searchPlaceholder = "Ara...",
  searchValue = "",
  searchMaxLength,
  onSearch,
  showSort = false,
  sortOptions = [],
  selectedSortValue = null,
  onSort,
  filters = [],
  onFilterChange,
  onAddNew,
  addButtonLabel = i18n.t("pageHeader.defaultAddButton"),
  addButtonIcon,
  addButtonVariant = "contained",
  addButtonColor = "primary",
  addButtonLoading = false,
  addMenuOptions,
  showViewToggle = false,
  viewMode = "list",
  onViewModeChange,
  leftExtraContent,
  className = "",
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isFilterActionLoading, setIsFilterActionLoading] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [selectedSort, setSelectedSort] = useState<string | null>(
    selectedSortValue ?? null,
  );
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  const sortRef = useRef<HTMLDivElement>(null);

  // Sync selected sort when parent passes selectedSortValue (kontrollü kullanım)
  useEffect(() => {
    if (selectedSortValue !== undefined && selectedSortValue !== null) {
      setSelectedSort(selectedSortValue);
    }
  }, [selectedSortValue]);

  // Close sort dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(event.target as Node)) {
        setIsSortOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSortSelect = (option: SortOption) => {
    setSelectedSort(option.value);
    setIsSortOpen(false);
    if (onSort) {
      onSort(option);
    }
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filterValues).some(
    (value) =>
      value !== undefined &&
      value !== null &&
      value !== "" &&
      !(Array.isArray(value) && value.length === 0),
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearchValue(value);
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleFilterToggle = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const handleFilterApply = () => {
    if (isFilterActionLoading) return;
    setIsFilterActionLoading(true);
    if (onFilterChange) {
      onFilterChange(filterValues);
    }
    setIsFilterOpen(false);
    setTimeout(() => setIsFilterActionLoading(false), 400);
  };

  const handleFilterReset = () => {
    if (isFilterActionLoading) return;
    setIsFilterActionLoading(true);
    setFilterValues({});
    if (onFilterChange) {
      onFilterChange({});
    }
    setTimeout(() => setIsFilterActionLoading(false), 400);
  };

  const handleFilterFieldChange = (fieldName: string, value: any) => {
    setFilterValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const renderFilterField = (field: FilterField) => {
    const value = filterValues[field.name];

    switch (field.type) {
      case "dropdown": {
        const isMultiple = field.multiple || false;
        // For single select, find the selected option label
        // For multiple select, the Dropdown component handles the display internally
        const selectedOption = !isMultiple
          ? (field.options || []).find((opt) => opt.value === value)
          : undefined;
        return (
          <div key={field.name} className="filter-field">
            <Dropdown
              label={field.label}
              placeholder={field.placeholder}
              options={field.options || []}
              value={isMultiple ? value || [] : value || null}
              selectedLabel={selectedOption?.label}
              onSelect={(selectedValue) =>
                handleFilterFieldChange(field.name, selectedValue)
              }
              allowClear
              searchable={field.searchable}
              onSearch={field.onSearch}
              multiple={isMultiple}
            />
          </div>
        );
      }

      case "date":
        return (
          <div key={field.name} className="filter-field">
            <label className="filter-field__label">{field.label}</label>
            <DatePicker
              value={
                value instanceof Date ? value : value ? new Date(value) : null
              }
              onChange={(val) => handleFilterFieldChange(field.name, val)}
              placeholder={field.placeholder || i18n.t("pageHeader.datePlaceholder")}
              allowClear
            />
          </div>
        );

      case "daterange": {
        const rangeValue = (value as [Date | null, Date | null]) || [
          null,
          null,
        ];
        return (
          <div
            key={field.name}
            className="filter-field filter-field--daterange"
          >
            <label className="filter-field__label">{field.label}</label>
            <div className="filter-field__daterange-inputs">
              <DatePicker
                value={rangeValue[0]}
                onChange={(val) =>
                  handleFilterFieldChange(field.name, [
                    val as Date | null,
                    rangeValue[1],
                  ])
                }
                placeholder={i18n.t("pageHeader.startDate")}
                allowClear
              />
              <span className="filter-field__daterange-separator">-</span>
              <DatePicker
                value={rangeValue[1]}
                onChange={(val) =>
                  handleFilterFieldChange(field.name, [
                    rangeValue[0],
                    val as Date | null,
                  ])
                }
                placeholder={i18n.t("pageHeader.endDate")}
                allowClear
              />
            </div>
          </div>
        );
      }

      case "text":
        return (
          <div key={field.name} className="filter-field">
            <Input
              label={field.label}
              type="text"
              value={value || ""}
              onChange={(e) =>
                handleFilterFieldChange(field.name, e.target.value)
              }
              placeholder={field.placeholder}
            />
          </div>
        );

      case "number":
        return (
          <div key={field.name} className="filter-field">
            {field.suffix ? (
              <>
                <label className="filter-field__label">{field.label}</label>
                <div className="filter-field__number-with-suffix">
                  <input
                    className="filter-field__number-input"
                    type="number"
                    value={value ?? ""}
                    min={field.min}
                    max={field.max}
                    onChange={(e) =>
                      handleFilterFieldChange(field.name, e.target.value)
                    }
                    placeholder={field.placeholder}
                  />
                  <span className="filter-field__number-suffix">
                    {field.suffix}
                  </span>
                </div>
              </>
            ) : (
              <Input
                label={field.label}
                type="number"
                value={value || ""}
                onChange={(e) =>
                  handleFilterFieldChange(field.name, e.target.value)
                }
                placeholder={field.placeholder}
              />
            )}
          </div>
        );

      case "range": {
        const rangeVal = (value as [number, number]) || [
          field.min ?? 0,
          field.max ?? 100,
        ];
        const min = field.min ?? 0;
        const max = field.max ?? 100;
        const unit = field.unit ?? "";
        const pctLeft = ((rangeVal[0] - min) / (max - min)) * 100;
        const pctRight = ((rangeVal[1] - min) / (max - min)) * 100;
        return (
          <div key={field.name} className="filter-field">
            <div className="filter-field__range-header">
              <label className="filter-field__label">{field.label}</label>
              <span className="filter-field__range-value">
                {rangeVal[0]} - {rangeVal[1]} {unit}
              </span>
            </div>
            <div className="filter-field__range-slider">
              <div className="filter-field__range-track">
                <div
                  className="filter-field__range-fill"
                  style={{
                    left: `${pctLeft}%`,
                    width: `${pctRight - pctLeft}%`,
                  }}
                />
              </div>
              <input
                type="range"
                className="filter-field__range-input filter-field__range-input--min"
                min={min}
                max={max}
                value={rangeVal[0]}
                onChange={(e) => {
                  const v = Math.min(Number(e.target.value), rangeVal[1]);
                  handleFilterFieldChange(field.name, [v, rangeVal[1]]);
                }}
              />
              <input
                type="range"
                className="filter-field__range-input filter-field__range-input--max"
                min={min}
                max={max}
                value={rangeVal[1]}
                onChange={(e) => {
                  const v = Math.max(Number(e.target.value), rangeVal[0]);
                  handleFilterFieldChange(field.name, [rangeVal[0], v]);
                }}
              />
              <div className="filter-field__range-labels">
                <span>{min}</span>
                <span>{max}</span>
              </div>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className={`page-header ${className}`}>
      {/* Title */}
      {title && <h1 className="page-header__title">{title}</h1>}

      {/* Action Bar */}
      <div className="page-header__actions">
        {/* Left Section: Search, Sort, Filter */}
        <div className="page-header__actions-left">
          {/* Search */}
          {onSearch && (
            <div className="page-header__search">
              <Input
                placeholder={searchPlaceholder}
                value={localSearchValue}
                onChange={handleSearchChange}
                startIcon={<SearchIcon width={20} height={20} />}
                maxLength={searchMaxLength}
              />
            </div>
          )}

          {/* Sort */}
          {showSort && sortOptions.length > 0 && (
            <div className="page-header__sort" ref={sortRef}>
              <button
                className={`page-header__sort-btn ${isSortOpen ? "active" : ""} ${selectedSort ? "has-selection" : ""}`}
                onClick={() => setIsSortOpen(!isSortOpen)}
              >
                <ArrowsSortIcon width={20} height={20} />
              </button>
              {isSortOpen && (
                <div className="page-header__sort-dropdown">
                  {sortOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`page-header__sort-option ${selectedSort === option.value ? "selected" : ""}`}
                      onClick={() => handleSortSelect(option)}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Extra content (e.g. library section sorts) */}
          {leftExtraContent}

          {/* Filter Toggle */}
          {filters && filters.length > 0 && (
            <button
              className={`page-header__filter-btn ${isFilterOpen ? "active" : ""} ${hasActiveFilters ? "has-filters" : ""}`}
              onClick={handleFilterToggle}
            >
              {isFilterOpen ? (
                <CloseIcon width={20} height={20} />
              ) : (
                <AdjustmentsIcon width={20} height={20} />
              )}
              <span className="page-header__filter-text">Filtre</span>
            </button>
          )}
        </div>

        {/* Right Section: View Toggle and Add Button */}
        <div className="page-header__actions-right">
          {/* View Toggle */}
          {showViewToggle && onViewModeChange && (
            <div className="page-header__view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => onViewModeChange("grid")}
                title="Card View"
              >
                <CardViewIcon width={20} height={20} />
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => onViewModeChange("list")}
                title="List View"
              >
                <ListViewIcon width={20} height={20} />
              </button>
            </div>
          )}

          {/* Add Button or Add Dropdown */}
          {addMenuOptions && addMenuOptions.length > 0 ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<PlusIcon width={20} height={20} />}
              dropdownItems={addMenuOptions.map((opt) => ({
                label: opt.label,
                value: opt.label,
                onClick: opt.onClick,
              }))}
            >
              {addButtonLabel}
            </Button>
          ) : onAddNew ? (
            <Button
              variant={addButtonVariant}
              color={addButtonColor}
              onClick={onAddNew}
              startIcon={addButtonIcon ?? <PlusIcon width={20} height={20} />}
              loading={addButtonLoading}
            >
              {addButtonLabel}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Filter Panel - always in DOM when filters exist, visibility via class for smooth transition */}
      {filters && filters.length > 0 && (
        <div
          className={`page-header__filter-panel ${isFilterOpen ? "page-header__filter-panel--open" : ""}`}
        >
          <div className="filter-panel">
            {/* Filter Fields Grid */}
            <div className="filter-panel__fields">
              {filters.map((field) => renderFilterField(field))}
            </div>

            {/* Filter Actions */}
            <div className="filter-panel__actions">
              <Button
                variant="text"
                color="secondary"
                onClick={handleFilterReset}
                disabled={isFilterActionLoading}
                loading={isFilterActionLoading}
              >
                Sıfırla
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleFilterApply}
                disabled={isFilterActionLoading}
              >
                Filtrele
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageHeader;
